<script>
import { provide, watch } from 'vue';
import { mapGetters } from 'vuex';

// PageInfo Ctrl
import { usePageInfo } from '@/plugins/page-info';

import { applicationStoreTypes } from '@/store/types';

// Components
import FeedbackScreen from '@/components/layout/FeedbackScreen.vue';
import SwordHeader from '@/components/layout/SWORDHeader.vue';
import SwordFooter from '@/components/layout/SWORDFooter.vue';
import SupportBanner from '@/components/layout/SupportBanner.vue';
import ProgramProgressBar from '@/components/enrolment/ProgramProgressBar.vue';
import PageTitle from './PageTitle.vue';

export default {
  name: 'Layout',

  components: {
    PageTitle,
    SwordFooter,
    SwordHeader,
    FeedbackScreen,
    SupportBanner,
    ProgramProgressBar,
  },

  props: {
    showMenu: {
      type: Boolean,
      default: true,
    },
    showBrandLogo: {
      type: Boolean,
      default: true,
    },
    footerLinks: {
      type: Object,
      default: undefined,
    },
    footerMinimal: Boolean,
  },

  setup() {
    const { pageInfo, resetPageInfo } = usePageInfo();

    provide('pageInfo', pageInfo);

    watch(() => pageInfo.isLoading, val => {
      if (val) window.scrollTo(0, 0);
    });

    return {
      pageInfo,
      resetPageInfo,
    };
  },

  computed: {
    ...mapGetters({
      clientToggles: 'client/getClientToggles',
      isDptGo: applicationStoreTypes.getters.IS_DPT_GO,
    }),
    isShowSupportBanner() {
      return this.clientToggles.show_support_mode ?? false;
    },
    showPageTitle() {
      return this.pageInfo.title || this.pageInfo.subTitle || this.pageInfo.currentPageName;
    },
    isShowProgressBar() {
      return !this.isDptGo;
    },
  },
};
</script>

<template>
  <div class="layout">
    <support-banner v-if="isShowSupportBanner" />

    <sword-header
      :show-menu="showMenu"
      :show-brand-logo="showBrandLogo"
    />

    <main class="layout__main flex-col-full">
      <feedback-screen
        v-if="pageInfo.isLoading"
        type="loading"
        :title="$t(pageInfo.loadTitle || 'onboarding_message_just_a_moment')"
        :text="$t(pageInfo.loadSubTitle || 'onboarding2_clinical_loading_message')"
      />

      <div v-show="!pageInfo.isLoading" class="flex-col-full">
        <slot>
          <program-progress-bar v-if="isShowProgressBar" />
        </slot>
        <page-title v-if="showPageTitle" class="layout-page-title" show-back />
        <router-view></router-view>
      </div>
    </main>
    <sword-footer :links="footerLinks" :minimal="footerMinimal" />
  </div>
</template>

<style lang="scss" scoped>
.layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  &__main {
    position: relative;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 2.5rem;
    width: 100%;
    min-height: 400px;
    max-width: 688px;
    margin: auto;

    @media screen and (min-width: $max-smartphone-size) {
      padding-left: 1.5rem;
      padding-right: 1.5rem;
    }
  }

  .layout-page-title {
    margin-top: 2rem;
  }
}

.flex-col-full {
  display: flex;
  flex-direction: column;
  flex: 1;
}
</style>
